//#region assets/vp-category-select.ts
/**
* Accessory category listbox for the kenteken config section.
*
* Expects markup from `snippets/vp-category-select.liquid`.
*/
var VpCategorySelect = class extends HTMLElement {
	/** @type {HTMLButtonElement | null} */
	#trigger = null;
	/** @type {HTMLInputElement | null} */
	#hiddenInput = null;
	/** @type {HTMLElement | null} */
	#list = null;
	/** @type {HTMLElement | null} */
	#label = null;
	/** @type {HTMLButtonElement[]} */
	#options = [];
	/** @type {number} */
	#activeIndex = -1;
	/** @type {HTMLButtonElement | null} */
	#selectedOption = null;
	/** @type {(event: MouseEvent) => void} */
	#onDocumentPointerDown = (event) => {
		if (!this.open) return;
		if (event.target instanceof Node && this.contains(event.target)) return;
		this.close();
	};
	connectedCallback() {
		this.#trigger = this.querySelector("[data-vp-category-trigger]");
		this.#hiddenInput = this.querySelector("[data-vp-category-value]");
		this.#list = this.querySelector("[data-vp-category-list]");
		this.#label = this.querySelector("[data-vp-category-label]");
		this.#options = Array.from(this.querySelectorAll("[data-vp-category-option]"));
		if (!this.#trigger || !this.#hiddenInput || !this.#list) return;
		this.#trigger.addEventListener("click", () => {
			if (this.open) this.close();
			else this.openPanel();
		});
		this.#trigger.addEventListener("keydown", (event) => this.#onTriggerKeydown(event));
		this.#list.addEventListener("click", (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			const option = target.closest("[data-vp-category-option]");
			if (!(option instanceof HTMLButtonElement)) return;
			this.#selectOption(option);
		});
		this.#list.addEventListener("keydown", (event) => this.#onListKeydown(event));
		this.#syncHasValue();
	}
	disconnectedCallback() {
		document.removeEventListener("pointerdown", this.#onDocumentPointerDown, true);
	}
	get open() {
		return this.hasAttribute("open");
	}
	/** @returns {string} */
	get value() {
		return this.#hiddenInput?.value || "";
	}
	/**
	* Native-select compatibility for kenteken-panel `#categoryUrl`.
	* @returns {HTMLButtonElement[]}
	*/
	get selectedOptions() {
		return this.#selectedOption ? [this.#selectedOption] : [];
	}
	openPanel() {
		if (this.open) return;
		this.setAttribute("open", "");
		this.#trigger?.setAttribute("aria-expanded", "true");
		document.addEventListener("pointerdown", this.#onDocumentPointerDown, true);
		const selectedIndex = this.#options.findIndex((option) => option.getAttribute("aria-selected") === "true");
		this.#setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
		requestAnimationFrame(() => this.#list?.focus());
	}
	close({ restoreFocus = true } = {}) {
		if (!this.open) return;
		this.removeAttribute("open");
		this.#trigger?.setAttribute("aria-expanded", "false");
		document.removeEventListener("pointerdown", this.#onDocumentPointerDown, true);
		this.#setActiveIndex(-1);
		if (restoreFocus) this.#trigger?.focus();
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onTriggerKeydown(event) {
		if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			this.openPanel();
		}
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onListKeydown(event) {
		const max = this.#options.length - 1;
		if (event.key === "Escape") {
			event.preventDefault();
			this.close();
			return;
		}
		if (event.key === "ArrowDown") {
			event.preventDefault();
			this.#setActiveIndex(Math.min(max, this.#activeIndex + 1));
			return;
		}
		if (event.key === "ArrowUp") {
			event.preventDefault();
			this.#setActiveIndex(Math.max(0, this.#activeIndex - 1));
			return;
		}
		if (event.key === "Home") {
			event.preventDefault();
			this.#setActiveIndex(0);
			return;
		}
		if (event.key === "End") {
			event.preventDefault();
			this.#setActiveIndex(max);
			return;
		}
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			const option = this.#options[this.#activeIndex];
			if (option) this.#selectOption(option);
		}
	}
	/**
	* @param {number} index
	*/
	#setActiveIndex(index) {
		this.#activeIndex = index;
		this.#options.forEach((option, i) => {
			option.classList.toggle("is-active", i === index);
		});
		if (index >= 0) this.#options[index]?.scrollIntoView({ block: "nearest" });
	}
	/**
	* @param {HTMLButtonElement} option
	*/
	#selectOption(option) {
		const value = option.dataset.value || "";
		const label = option.dataset.label || option.textContent?.trim() || "";
		this.#options.forEach((item) => {
			item.setAttribute("aria-selected", item === option ? "true" : "false");
		});
		this.#selectedOption = option;
		if (this.#hiddenInput) this.#hiddenInput.value = value;
		if (this.#label) this.#label.textContent = label;
		this.#syncHasValue();
		this.close();
		this.dispatchEvent(new Event("change", { bubbles: true }));
	}
	#syncHasValue() {
		this.toggleAttribute("data-has-value", Boolean(this.#hiddenInput?.value));
	}
};
if (!customElements.get("vp-category-select")) customElements.define("vp-category-select", VpCategorySelect);
//#endregion
